<script setup>
import {defineModel, defineProps, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue'

const modelValue = defineModel({required: true})
const textareaField = ref(null)

const props = defineProps({
  readonly: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: 'the-textarea',
  },
  rows: {
    type: String,
    default: '3',
  },
})

let resizeObserver

const autoResize = () => {
  if (textareaField.value) {
    const el = textareaField.value

    el.style.height = 'auto'
    const newHeight = el.scrollHeight + 3

    if (newHeight < 40) {
      el.style.height = '32px'
    } else {
      el.style.height = `${newHeight}px`
    }
  }
}

const waitForVisible = async () => {
  const el = textareaField.value
  if (!el) return

  let retries = 10

  while (retries-- > 0) {
    await nextTick()
    if (el.offsetHeight > 0) {
      autoResize()
      return
    }
    await new Promise(resolve => setTimeout(resolve, 50))
  }
}

onMounted(async () => {
  await waitForVisible()
  autoResize()

  if (textareaField.value) {
    resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        autoResize()
      })
    })
    resizeObserver.observe(textareaField.value)
  }
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

// Watch
watch(modelValue, async () => {
  await nextTick()
  autoResize()
})
</script>

<template>
  <textarea
    ref="textareaField"
    v-model="modelValue"
    :placeholder="props.placeholder"
    :id="props.id"
    :rows="props.rows"
    @input="autoResize"
    :readonly="props.readonly"
    :disabled="props.disabled"
  />
</template>
